// NotificationBell.js
import React, { useState, useEffect } from "react";
import notificationService from "../../services/NotificationService";
import "./NotificationBell.css";

const NotificationBell = ({ onNotificationClick, size = "medium" }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Initialize notification service
    const initializeService = async () => {
      try {
        await notificationService.initialize();
        setUnreadCount(notificationService.getUnreadCount());
      } catch (error) {
        console.error("Failed to initialize notification service:", error);
      }
    };

    initializeService();

    // Listen for unread count changes
    const unsubscribeUnreadCount = notificationService.addEventListener(
      "unreadCountChanged",
      (count) => {
        setUnreadCount(count);
        // Trigger animation for new notifications
        if (count > unreadCount) {
          setIsAnimating(true);
          setTimeout(() => setIsAnimating(false), 600);
        }
      }
    );

    // Listen for notifications loaded events
    const unsubscribeNotificationsLoaded = notificationService.addEventListener(
      "notificationsLoaded",
      (data) => {
        // Update unread count when notifications are loaded
        setUnreadCount(notificationService.getUnreadCount());
      }
    );

    // Listen for new notifications to trigger animation
    const unsubscribeNewNotification = notificationService.addEventListener(
      "notificationCreated",
      (notification) => {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 600);
      }
    );

    // Cleanup on unmount
    return () => {
      unsubscribeUnreadCount();
      unsubscribeNotificationsLoaded();
      unsubscribeNewNotification();
    };
  }, [unreadCount]);

  const handleClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case "small":
        return "notification-bell-small";
      case "large":
        return "notification-bell-large";
      default:
        return "notification-bell-medium";
    }
  };

  return (
    <div
      className={`notification-bell ${getSizeClass()} ${
        isAnimating ? "notification-bell-animate" : ""
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Notifications ${
        unreadCount > 0 ? `(${unreadCount} unread)` : ""
      }`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "24px",
        minHeight: "24px",
        position: "relative",
      }}
    >
      <div className="notification-bell-icon">
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="notification-bell-svg"
          style={{
            width: "20px",
            height: "20px",
            color: "#4c4949da",
          }}
        >
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
      </div>

      {unreadCount > 0 && (
        <div className="notification-bell-badge">
          <span className="notification-bell-count">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
